import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  Key,
  Settings,
  FileText,
  GitCompare,
  ChevronRight,
  Loader2,
  FolderOpen,
  Copy,
  GitBranch,
  Users,
  KeyRound,
  Terminal,
  Clock,
  CheckCircle2,
  Circle,
  ArrowRight,
  Activity,
} from 'lucide-react';
import { useProjects, type Environment } from '../hooks/useProjects';
import { useAuditLogs, getActionCategory, type AuditLogEntry } from '../hooks/useAuditLogs';
import { useTokens } from '../hooks/useTokens';
import { useTeamMembers } from '../hooks/useTeams';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/toast';
import { Command } from '@/components/ui/code-block';
import { apiClient } from '../api/client';

// Hook to fetch secret counts for all environments
function useSecretCounts(projectId: string | null, environments: Environment[]) {
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!projectId || environments.length === 0) {
      return;
    }

    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Setting loading state before async fetch is valid
    setIsLoading(true);

    const fetchCounts = async () => {
      const results: Record<string, number> = {};

      await Promise.all(
        environments.map(async (env) => {
          try {
            const response = await apiClient.get<{ secrets: Array<{ key: string; value: string }> }>(
              `/api/v1/projects/${projectId}/environments/${env.name}/secrets/export`
            );
            results[env.name] = response.data.secrets.length;
          } catch {
            results[env.name] = 0;
          }
        })
      );

      if (!cancelled) {
        setCounts(results);
        setIsLoading(false);
      }
    };

    fetchCounts();

    return () => {
      cancelled = true;
    };
  }, [projectId, environments]);

  return { counts, isLoading };
}

export function ProjectOverviewPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const toast = useToast();
  const {
    projects,
    isLoadingProjects,
    environments,
    isLoadingEnvironments,
    selectProject,
    primaryTeamId,
  } = useProjects();

  const project = projects.find((p) => p.id === projectId);
  const teamId = project?.team_id || primaryTeamId;

  // Fetch data using existing hooks
  const { counts: secretCounts, isLoading: isLoadingSecretCounts } = useSecretCounts(
    projectId || null,
    environments
  );
  const { tokens, isLoading: isLoadingTokens } = useTokens();
  const { members, isLoading: isLoadingMembers } = useTeamMembers(teamId || null);
  const { logs: auditLogs, isLoading: isLoadingAudit } = useAuditLogs({
    teamId: teamId || undefined,
    pageSize: 10,
  });

  // Filter audit logs for this project
  const projectAuditLogs = useMemo(() => {
    return auditLogs
      .filter((log) => log.projectId === projectId)
      .slice(0, 5);
  }, [auditLogs, projectId]);

  // Filter tokens for this project
  const projectTokens = useMemo(() => {
    return tokens.filter((t) => t.project_ids.includes(projectId || ''));
  }, [tokens, projectId]);

  // Calculate sync status (missing/mismatched secrets)
  const syncStatus = useMemo(() => {
    if (isLoadingSecretCounts || environments.length < 2) {
      return { missing: 0, total: 0 };
    }

    const allCounts = Object.values(secretCounts);
    const maxCount = Math.max(...allCounts, 0);
    const missing = allCounts.filter((c) => c < maxCount).length;

    return { missing, total: environments.length };
  }, [secretCounts, isLoadingSecretCounts, environments]);

  // Get last modified from audit logs
  const lastModified = projectAuditLogs[0];

  // Total secrets across all environments
  const totalSecrets = Object.values(secretCounts).reduce((a, b) => a + b, 0);

  useEffect(() => {
    if (projectId && project) selectProject(projectId);
  }, [projectId, project, selectProject]);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  // Check if project is empty (no secrets)
  const isEmptyProject = !isLoadingSecretCounts && totalSecrets === 0;

  if (isLoadingProjects) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 text-muted-foreground animate-spin" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="max-w-4xl mx-auto text-center py-16">
        <FolderOpen className="h-6 w-6 text-muted-foreground mx-auto mb-3" />
        <p className="text-sm text-muted-foreground mb-4">Project not found</p>
        <Button size="sm" variant="outline" asChild>
          <Link to="/projects">View all projects</Link>
        </Button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="max-w-6xl mx-auto space-y-8"
    >
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex items-start justify-between"
      >
        <div>
          <h1 className="text-xl font-semibold">{project.name}</h1>
          {project.description && (
            <p className="text-sm text-muted-foreground mt-1">{project.description}</p>
          )}
          <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
            <span>Created {new Date(project.created_at).toLocaleDateString()}</span>
            <button
              onClick={() => copyToClipboard(projectId || '', 'Project ID')}
              className="flex items-center gap-1.5 hover:text-foreground transition-colors"
            >
              <span className="font-mono">{projectId?.slice(0, 8)}...</span>
              <Copy className="h-3 w-3" />
            </button>
            {lastModified && (
              <span className="flex items-center gap-1.5">
                <Clock className="h-3 w-3" />
                Last activity {formatRelativeTime(lastModified.createdAt)}
              </span>
            )}
          </div>
        </div>
        <Button size="sm" variant="outline" asChild>
          <Link to={`/projects/${projectId}/settings`}>
            <Settings className="h-3.5 w-3.5 mr-1.5" />
            Settings
          </Link>
        </Button>
      </motion.div>

      {/* Stats Grid */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="grid grid-cols-4 gap-4"
      >
        <StatCard
          label="Environments"
          value={isLoadingEnvironments ? '—' : environments.length}
          icon={GitBranch}
          href={`/projects/${projectId}/secrets`}
        />
        <StatCard
          label="Secrets"
          value={isLoadingSecretCounts ? '—' : totalSecrets}
          icon={Key}
          href={`/projects/${projectId}/secrets`}
          status={
            syncStatus.missing > 0
              ? { type: 'warning', text: `${syncStatus.missing} env behind` }
              : undefined
          }
        />
        <StatCard
          label="Team Members"
          value={isLoadingMembers ? '—' : members.length}
          icon={Users}
          href={`/projects/${projectId}/team`}
        />
        <StatCard
          label="API Tokens"
          value={isLoadingTokens ? '—' : projectTokens.length}
          icon={KeyRound}
          href="/tokens"
        />
      </motion.div>

      {/* Getting Started Checklist (for empty projects) */}
      {isEmptyProject && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.15 }}
        >
          <GettingStartedChecklist
            projectId={projectId!}
            hasEnvironments={environments.length > 0}
            hasSecrets={totalSecrets > 0}
            hasTokens={projectTokens.length > 0}
          />
        </motion.div>
      )}

      {/* Main Content Grid */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
        className="grid grid-cols-3 gap-6"
      >
        {/* Left Column - 2/3 width */}
        <div className="col-span-2 space-y-6">
          {/* Environments with Secret Counts */}
          <Section title="Environments" icon={GitBranch}>
            {isLoadingEnvironments ? (
              <LoadingState />
            ) : environments.length === 0 ? (
              <EmptyState message="No environments yet" />
            ) : (
              <div className="border border-border rounded-lg divide-y divide-border">
                {environments.map((env) => {
                  const count = secretCounts[env.name] ?? 0;
                  const maxCount = Math.max(...Object.values(secretCounts), 0);
                  const isBehind = count < maxCount && maxCount > 0;
                  const parentEnv = env.inherits_from
                    ? environments.find((e: Environment) => e.id === env.inherits_from)
                    : null;

                  return (
                    <Link
                      key={env.id}
                      to={`/projects/${projectId}/secrets?env=${env.name}`}
                      className="flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-2 h-2 rounded-full ${
                            isBehind ? 'bg-warning' : 'bg-success'
                          }`}
                        />
                        <span className="text-sm font-medium capitalize">{env.name}</span>
                        {parentEnv && (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <GitBranch className="h-3 w-3" />
                            from {parentEnv.name}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground tabular-nums">
                          {isLoadingSecretCounts ? '...' : `${count} secrets`}
                        </span>
                        {isBehind && (
                          <span className="text-xs text-warning bg-warning/10 px-1.5 py-0.5 rounded">
                            {maxCount - count} missing
                          </span>
                        )}
                        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </Section>

          {/* Recent Activity */}
          <Section title="Recent Activity" icon={Activity}>
            {isLoadingAudit ? (
              <LoadingState />
            ) : projectAuditLogs.length === 0 ? (
              <EmptyState message="No recent activity" />
            ) : (
              <div className="border border-border rounded-lg divide-y divide-border">
                {projectAuditLogs.map((log) => (
                  <ActivityRow key={log.id} log={log} />
                ))}
                <Link
                  to={`/projects/${projectId}/audit`}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors"
                >
                  View all activity
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            )}
          </Section>
        </div>

        {/* Right Column - 1/3 width */}
        <div className="space-y-6">
          {/* CLI Quick Start */}
          <Section title="CLI Quick Start" icon={Terminal}>
            <div className="space-y-4">
              <div>
                <div className="text-xs text-muted-foreground mb-2">Pull secrets</div>
                <Command>keyenv pull --env production</Command>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-2">Run with secrets</div>
                <Command>keyenv run -- npm start</Command>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-2">Push local .env</div>
                <Command>keyenv push --env development</Command>
              </div>
            </div>
          </Section>

          {/* Quick Links */}
          <Section title="Quick Actions" icon={ArrowRight}>
            <div className="border border-border rounded-lg divide-y divide-border">
              <QuickLink
                to={`/projects/${projectId}/secrets`}
                icon={Key}
                label="Manage Secrets"
              />
              <QuickLink
                to={`/projects/${projectId}/compare`}
                icon={GitCompare}
                label="Compare Environments"
              />
              <QuickLink
                to={`/projects/${projectId}/audit`}
                icon={FileText}
                label="View Audit Log"
              />
              <QuickLink to="/tokens" icon={KeyRound} label="API Tokens" />
            </div>
          </Section>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Components

function StatCard({
  label,
  value,
  icon: Icon,
  href,
  status,
}: {
  label: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  status?: { type: 'warning' | 'success'; text: string };
}) {
  return (
    <Link
      to={href}
      className="bg-card border border-border rounded-lg p-4 hover:border-primary/30 transition-colors group"
    >
      <div className="flex items-center justify-between mb-2">
        <Icon className="h-4 w-4 text-muted-foreground" />
        {status && (
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded ${
              status.type === 'warning'
                ? 'bg-warning/10 text-warning'
                : 'bg-success/10 text-success'
            }`}
          >
            {status.text}
          </span>
        )}
      </div>
      <div className="text-2xl font-semibold">{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
    </Link>
  );
}

function Section({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-medium">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-8 border border-border rounded-lg">
      <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex items-center justify-center py-8 border border-border rounded-lg text-sm text-muted-foreground">
      {message}
    </div>
  );
}

function ActivityRow({ log }: { log: AuditLogEntry }) {
  const category = getActionCategory(log.action);
  const categoryColors = {
    create: 'text-success',
    read: 'text-primary',
    update: 'text-warning',
    delete: 'text-destructive',
    other: 'text-muted-foreground',
  };

  const actionLabels: Record<string, string> = {
    'secret.created': 'created secret',
    'secret.updated': 'updated secret',
    'secret.deleted': 'deleted secret',
    'secret.read': 'read secret',
    'environment.created': 'created environment',
    'environment.deleted': 'deleted environment',
    'token.created': 'created token',
    'token.revoked': 'revoked token',
    'project.updated': 'updated project',
  };

  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <div className={`w-1.5 h-1.5 rounded-full ${categoryColors[category]} bg-current`} />
      <div className="flex-1 min-w-0">
        <span className="text-sm">
          <span className={categoryColors[category]}>
            {actionLabels[log.action] || log.action}
          </span>
          {log.resourceName && (
            <span className="text-muted-foreground">
              {' '}
              <span className="font-mono">{log.resourceName}</span>
            </span>
          )}
          {log.environmentName && (
            <span className="text-muted-foreground"> in {log.environmentName}</span>
          )}
        </span>
      </div>
      <span className="text-xs text-muted-foreground">{formatRelativeTime(log.createdAt)}</span>
    </div>
  );
}

function QuickLink({
  to,
  icon: Icon,
  label,
}: {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors group"
    >
      <Icon className="h-4 w-4 text-muted-foreground" />
      <span className="text-sm flex-1">{label}</span>
      <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
    </Link>
  );
}

function GettingStartedChecklist({
  projectId,
  hasEnvironments,
  hasSecrets,
  hasTokens,
}: {
  projectId: string;
  hasEnvironments: boolean;
  hasSecrets: boolean;
  hasTokens: boolean;
}) {
  const steps = [
    {
      done: hasEnvironments,
      label: 'Create an environment',
      description: 'Start with development, staging, and production',
      href: `/projects/${projectId}/secrets`,
    },
    {
      done: hasSecrets,
      label: 'Add your first secret',
      description: 'Import from .env or add manually',
      href: `/projects/${projectId}/secrets`,
    },
    {
      done: hasTokens,
      label: 'Create an API token',
      description: 'For CI/CD and local development',
      href: '/tokens',
    },
  ];

  const completedCount = steps.filter((s) => s.done).length;

  if (completedCount === steps.length) return null;

  return (
    <div className="bg-primary/5 border border-primary/20 rounded-lg p-5">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-primary/10 rounded-lg">
          <CheckCircle2 className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h3 className="font-medium">Getting Started</h3>
          <p className="text-xs text-muted-foreground">
            {completedCount}/{steps.length} steps completed
          </p>
        </div>
      </div>
      <div className="space-y-3">
        {steps.map((step, i) => (
          <Link
            key={i}
            to={step.href}
            className={`flex items-start gap-3 p-3 rounded-lg transition-colors ${
              step.done
                ? 'bg-success/5 border border-success/20'
                : 'bg-card border border-border hover:border-primary/30'
            }`}
          >
            {step.done ? (
              <CheckCircle2 className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
            ) : (
              <Circle className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-medium ${step.done ? 'text-success' : ''}`}>
                {step.label}
              </div>
              <div className="text-xs text-muted-foreground">{step.description}</div>
            </div>
            {!step.done && <ArrowRight className="h-4 w-4 text-muted-foreground mt-0.5" />}
          </Link>
        ))}
      </div>
    </div>
  );
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}
